import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Message as MessageIcon,
  Timer as TimerIcon,
  VolumeUp as AudioIcon,
  Person as PersonIcon,
  SmartToy as BotIcon
} from '@mui/icons-material';

import conversationService, { type ConversationAnalytics } from '../../services/conversationService';

interface ConversationAnalyticsProps {
  conversationId: string;
}

const ConversationAnalyticsComponent: React.FC<ConversationAnalyticsProps> = ({ conversationId }) => {
  const [analytics, setAnalytics] = useState<ConversationAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, [conversationId]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await conversationService.getConversationAnalytics(conversationId);
      setAnalytics(data);
    } catch (err) {
      setError('Error al cargar las analíticas de la conversación');
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes?: number): string => {
    if (!minutes) return 'N/A';

    if (minutes < 60) {
      return `${Math.round(minutes)}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatResponseTime = (seconds?: number): string => {
    if (!seconds) return 'N/A';

    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getEngagementLevel = (score: number): { label: string; color: 'success' | 'warning' | 'error' } => {
    if (score >= 7) return { label: 'Alto', color: 'success' };
    if (score >= 4) return { label: 'Medio', color: 'warning' };
    return { label: 'Bajo', color: 'error' };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body2" ml={2}>
          Cargando analíticas...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No hay analíticas disponibles para esta conversación.
      </Alert>
    );
  }

  const engagementLevel = getEngagementLevel(analytics.customer_engagement.messages_per_session);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Analíticas de Conversación
      </Typography>

      <Grid container spacing={3}>
        {/* Overview Cards */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <MessageIcon color="primary" />
                <Typography variant="h6">
                  {analytics.total_messages}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total de Mensajes
              </Typography>
              <Box display="flex" gap={1} mt={1}>
                <Chip
                  icon={<PersonIcon />}
                  label={`${analytics.user_messages} usuario`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<BotIcon />}
                  label={`${analytics.bot_messages} bot`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TimerIcon color="primary" />
                <Typography variant="h6">
                  {formatDuration(analytics.conversation_duration_minutes)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Duración Total
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                Tiempo promedio de respuesta: {formatResponseTime(analytics.avg_response_time_seconds)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AudioIcon color="primary" />
                <Typography variant="h6">
                  {analytics.audio_messages_count}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Mensajes de Audio
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                Duración total: {Math.round(analytics.total_audio_duration_seconds)}s
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Detailed Analytics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tipos de Mensajes
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                {Object.entries(analytics.message_types_distribution).map(([type, count]) => (
                  <Box key={type}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="body2" textTransform="capitalize">
                        {conversationService.formatMessageType(type)}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {count}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(count / analytics.total_messages) * 100}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Engagement del Cliente
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Nivel de Engagement
                  </Typography>
                  <Chip
                    label={engagementLevel.label}
                    color={engagementLevel.color}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Mensajes por sesión
                  </Typography>
                  <Typography variant="h6">
                    {analytics.customer_engagement.messages_per_session}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Longitud promedio de mensaje
                  </Typography>
                  <Typography variant="h6">
                    {Math.round(analytics.customer_engagement.avg_message_length)} caracteres
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Usa mensajes de audio
                  </Typography>
                  <Chip
                    label={analytics.customer_engagement.has_audio_messages ? 'Sí' : 'No'}
                    color={analytics.customer_engagement.has_audio_messages ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Lead Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Lead
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Estado del Lead
                  </Typography>
                  <Chip
                    label={conversationService.formatLeadStatus(analytics.lead_status)}
                    sx={{
                      backgroundColor: conversationService.getLeadStatusColor(analytics.lead_status),
                      color: 'white',
                      mt: 0.5
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Proyecto de Interés
                  </Typography>
                  <Typography variant="body1">
                    {analytics.project_interest || 'No especificado'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConversationAnalyticsComponent;
